import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { userPreferencesService } from '@/services/userPreferencesService';
import { UserPreference } from '@/types/users';

interface UserPreferenceContextType {
  preference: UserPreference | null;
  loading: boolean;
  openProcessInNewTab: boolean;
  refreshPreference: () => Promise<void>;
}

const UserPreferenceContext = createContext<UserPreferenceContextType | undefined>(undefined);

export const UserPreferenceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token } = useAuth();
  const [preference, setPreference] = useState<UserPreference | null>(null);
  const [loading, setLoading] = useState(true);

  const loadPreference = useCallback(async () => {
    if (!token) {
      setLoading(false);
      setPreference(null);
      return;
    }

    try {
      const pref = await userPreferencesService.get();
      setPreference(pref);
    } catch (error) {
      console.error('Erro ao carregar preferência:', error);
      // Em caso de erro, usar valor padrão
      setPreference(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPreference();
  }, [loadPreference]);

  const refreshPreference = useCallback(async () => {
    await loadPreference();
  }, [loadPreference]);

  const openProcessInNewTab = preference?.open_process_in_new_tab === 'new';

  return (
    <UserPreferenceContext.Provider
      value={{
        preference,
        loading,
        openProcessInNewTab,
        refreshPreference,
      }}
    >
      {children}
    </UserPreferenceContext.Provider>
  );
};

const SAFE_FALLBACK: UserPreferenceContextType = {
  preference: null,
  loading: false,
  openProcessInNewTab: false,
  refreshPreference: async () => {},
};

export const useUserPreference = () => {
  const context = useContext(UserPreferenceContext);
  // Safe fallback para componentes renderizados fora do UserPreferenceProvider (ex: portal externo)
  if (context === undefined) return SAFE_FALLBACK;
  return context;
};
